import { useRuntimeConfig } from '#imports'
import type { RouteRecordRaw } from 'vue-router'

import { ROLES } from '@backmarket/nuxt-layer-buyback/components/OrderConversation/OrderConversation.constants'
import type { Features } from '@backmarket/nuxt-module-marketplace/features'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { ROUTES as AUTH_ROUTES } from '~/scopes/auth/route-names'
import { CMS } from '~/scopes/cms/routes-names'

import { ROUTE_NAMES } from './constants'

export default ({
  features,
  KILL_BUYBACK_FUNNEL,
}: {
  features: Features
  KILL_BUYBACK_FUNNEL: boolean
}): RouteRecordRaw[] => {
  const { market } = useMarketplace()
  const { FF_ENABLE_TRADE_IN_PRODUCT_PAGES } = useRuntimeConfig().public
  const isBuybackEnabled = features.buyback?.tradeInEnabled

  const checkBuybackRouteAvailability = () => {
    if (!isBuybackEnabled)
      return {
        message: 'Buyback funnel is not enabled for country',
      }

    if (KILL_BUYBACK_FUNNEL)
      return {
        message: 'Buyback funnel is disabled by kill switch',
      }

    return true
  }

  return [
    {
      path: 'buyback-funnel',
      component: () =>
        import('~/scopes/buyback/components/TheCatcher/TheCatcherPage.vue'),
      meta: {
        // this is necessary to show the 404 page with the default layout when the route does not exist
        layout:
          checkBuybackRouteAvailability() === true ? 'minimal' : 'default',
        owners: ['bot-squad-circularity-offer-front'],
        routeExists: checkBuybackRouteAvailability,
      },
      children: [
        {
          name: ROUTE_NAMES.DEVICE_ASSESSMENT,
          path: 'device/:slug/:id',
          component: () =>
            import('~/scopes/buyback/pages/DeviceAssessment.vue'),
          meta: {
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
        {
          name: ROUTE_NAMES.OFFER,
          path: 'device/:slug/:id/offer/:listingId',
          component: () => import('~/scopes/buyback/pages/Offer.vue'),
          meta: {
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
        {
          name: ROUTE_NAMES.NO_OFFER,
          path: 'device/:slug/:id/no-offer',
          component: () => import('~/scopes/buyback/pages/NoOffer.vue'),
          meta: {
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
        {
          name: ROUTE_NAMES.BANK,
          path: 'device/:slug/:id/offer/:listingId/bank',
          component: () => import('~/scopes/buyback/pages/Bank.vue'),
          meta: {
            auth: {
              required: true,
              redirection: {
                name: AUTH_ROUTES.AUTH.LOGIN,
                query: { bm_journey: CMS.BUYBACK },
              },
            },
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
        {
          name: ROUTE_NAMES.ADDRESS,
          path: 'device/:slug/:id/offer/:listingId/address',
          component: () => import('~/scopes/buyback/pages/Address.vue'),
          meta: {
            auth: {
              required: true,
              redirection: {
                name: AUTH_ROUTES.AUTH.LOGIN,
                query: { bm_journey: CMS.BUYBACK },
              },
            },
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
        {
          name: ROUTE_NAMES.SHIPPING,
          path: 'device/:slug/:id/offer/:listingId/address/:addressId/shipping',
          component: () => import('~/scopes/buyback/pages/Shipping.vue'),
          meta: {
            auth: {
              required: true,
              redirection: {
                name: AUTH_ROUTES.AUTH.LOGIN,
                query: { bm_journey: CMS.BUYBACK },
              },
            },
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
        {
          name: ROUTE_NAMES.CONFIRMATION,
          path: 'confirmation/:id',
          component: () => import('~/scopes/buyback/pages/Confirmation.vue'),
          meta: {
            auth: {
              required: true,
              redirection: {
                name: AUTH_ROUTES.AUTH.LOGIN,
                query: { bm_journey: CMS.BUYBACK },
              },
            },
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
      ],
    },
    {
      name: ROUTE_NAMES.CONVERSATION,
      path: 'dashboard/sales/:id/conversation',
      component: () =>
        import(
          '@backmarket/nuxt-layer-buyback/components/OrderConversation/OrderConversation.vue'
        ),
      meta: {
        auth: {
          required: true,
          redirection: {
            name: AUTH_ROUTES.AUTH.LOGIN,
            query: { bm_journey: CMS.BUYBACK },
          },
        },
        owners: ['bot-squad-circularity-offer-front'],
        // this is necessary to show the 404 page with the default layout when the route does not exist
        layout:
          checkBuybackRouteAvailability() === true ? 'minimal' : 'default',
        routeExists: checkBuybackRouteAvailability,
      },
      props: {
        role: ROLES.CUSTOMER,
      },
    },
    {
      name: ROUTE_NAMES.TRADE_IN_PRODUCT,
      path: 'buyback/:category/:slug/:id',
      component: () =>
        import('~/scopes/buyback/pages/ProductPage/ProductPage.vue'),
      meta: {
        owners: ['bot-squad-circularity-offer-front'],

        routeExists: () =>
          (FF_ENABLE_TRADE_IN_PRODUCT_PAGES.includes(market.countryCode) &&
            checkBuybackRouteAvailability() === true) ||
          false,
      },
    },
  ]
}
